/**
 * useAllUserMedia — unified library hook. Zwraca WSZYSTKIE zdjęcia/wideo user-a:
 *   - firm_creative_assets (logo, twarz, produkt, scena, paleta)
 *   - user_avatars (UGC studio — AI generated lub uploaded)
 *   - user_heygen_avatars (HeyGen photo/instant avatars — preview image)
 *   - user_media_assets (generic uploads z innych miejsc)
 *
 * Używany wszędzie gdzie pokazujemy "moją bibliotekę" — modal Zdjęcia referencyjne,
 * brand-assets dropdown w kreatorach, autopilot conditioning preview.
 */
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

export interface UnifiedMediaItem {
  id: string;
  uniqueKey: string;
  source: "firm_assets" | "avatar_ugc" | "avatar_heygen" | "media_upload";
  category: "logo" | "face" | "product" | "scene" | "video" | "audio" | "other";
  assetType: string | null;
  mediaType: "image" | "video" | "audio";
  title: string;
  description: string | null;
  fileUrl: string;
  thumbnailUrl: string | null;
  createdAt: string | null;
  metadata?: Record<string, unknown>;
}

export const CATEGORY_LABELS: Record<UnifiedMediaItem["category"], string> = {
  logo: "Logo",
  face: "Twarz / avatar",
  product: "Produkt",
  scene: "Scena / tło",
  video: "Video",
  audio: "Audio",
  other: "Inne",
};

export const SOURCE_LABELS: Record<UnifiedMediaItem["source"], string> = {
  firm_assets: "Biblioteka",
  avatar_ugc: "UGC",
  avatar_heygen: "HeyGen",
  media_upload: "Upload",
};

export function useAllUserMedia() {
  return useQuery<{ items: UnifiedMediaItem[] }>({
    queryKey: ["user-media-all"],
    queryFn: () => apiRequest("GET", "/api/user-media/all"),
    staleTime: 30_000,
  });
}
